import React from "react";
import Utils from 'Common/Utils';
import { ChartIcon, ColumnChartIcon, CombinedChartIcon, LineChartIcon } from 'assests/GmsIcons';
import { useCallback, useEffect, useRef, useState } from 'react';
import Menu from 'Components/DesignSystem/Menu/Menu';

type ChartTypesProps = {
    selectedItem: string;
    setSelectedItem: (selectedItem: string) => void;
    /**
     * Base for this instance's testids. One ChartTypes renders per chart card, so
     * the caller should pass a per-card value (e.g. keyed by metric name) — the
     * default keeps the legacy shared ids for callers that have not opted in yet.
     */
    'data-testid'?: string;
}

const CHART_TYPES: Array<{ type: string; Icon: React.ComponentType<{ selected: boolean }>; slot: number }> = [
    { type: "Line Chart", Icon: LineChartIcon, slot: 2 },
    { type: "Column Chart", Icon: ColumnChartIcon, slot: 3 },
    { type: "Line & Column", Icon: CombinedChartIcon, slot: 4 },
];

export const ChartTypes = ({ selectedItem, setSelectedItem, 'data-testid': baseTestId = 'components-charttypes' }: ChartTypesProps) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => Utils.run_callback_on_click_outside_ref(e, ref, () => setShowMenu(false)),
    []
  );

  useEffect(() => {
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu, handleClickOutside]);

  const handleChartSelection = (type: string) => {
    setShowMenu(false);
    setTimeout(() => {
      setSelectedItem(type);
    }, 0);
  };

  return (
    <div ref={ref} className="tw-relative tw-bg-surface">
      <div aria-label="Chart" role="button" data-testid={`${baseTestId}-div-1`}
        onClick={() => setShowMenu(prev => !prev)}
        className={`${showMenu ? 'tw-bg-surface-hover' : ''} hover:tw-bg-surface-hover tw-cursor-pointer tw-p-2 tw-ms-3 tw-rounded-[4px] tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-[6px]`}
      >
        <span className="icon-follow-ink tw-text-ink-secondary tw-flex tw-items-center"><ChartIcon /></span>
      </div>
      {showMenu && (
        <Menu
          variant="compact"
          className="tw-absolute tw-top-full tw-left-0 tw-w-48 tw-mt-2 tw-mx-3 tw-z-40"
          data-testid={`${baseTestId}-menu`}
          items={CHART_TYPES.map(({ type, Icon, slot }) => ({
            id: type,
            label: type,
            icon: <Icon selected={selectedItem === type} />,
            selected: selectedItem === type,
            onClick: () => handleChartSelection(type),
            'data-testid': `${baseTestId}-div-${slot}`,
          }))}
        />
      )}
    </div>
  );
};
